import React, { useState } from 'react';
import { MessageSquarePlus, X, Send, Loader2, AlertCircle, Bug, Lightbulb, MessageSquare, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const FeedbackWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('new'); // new, history
    const [type, setType] = useState('bug'); // bug, suggestion, other
    const [message, setMessage] = useState('');
    const { addFeedback, feedbacks, loading, refreshFeedbacks } = useFeedback();
    const { user } = useAuth();

    // Calcular feedbacks do usuário atual para o histórico
    const myFeedbacks = feedbacks.filter(f =>
        (user?.discordId && String(f.user?.discordId) === String(user.discordId)) ||
        (user?.id && String(f.user?.id) === String(user.id))
    );

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 1. Validação de Tamanho (50 caracteres)
        if (message.trim().length < 50) {
            toast.error('Por favor, descreva com mais detalhes (mínimo 50 caracteres).');
            return;
        }

        // 2. Rate Limiting (1 minuto)
        const lastFeedbackTime = localStorage.getItem('lastFeedbackTime');
        if (lastFeedbackTime) {
            const timeDiff = Date.now() - parseInt(lastFeedbackTime);
            if (timeDiff < 60000) { // 60 segundos
                const secondsLeft = Math.ceil((60000 - timeDiff) / 1000);
                toast.error(`Aguarde ${secondsLeft}s para enviar outro feedback.`);
                return;
            }
        }

        const result = await addFeedback({
            type,
            message
        });

        if (result.success) {
            toast.success('Feedback enviado com sucesso! Obrigado.');
            setMessage('');
            localStorage.setItem('lastFeedbackTime', Date.now().toString());
            setActiveTab('history'); // Muda para aba de histórico após enviar
        } else {
            toast.error('Erro ao enviar feedback. Tente novamente.');
        }
    };

    const getTypeIcon = (t) => {
        switch (t) {
            case 'bug': return <Bug className="h-4 w-4" />;
            case 'suggestion': return <Lightbulb className="h-4 w-4" />;
            default: return <MessageSquare className="h-4 w-4" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'resolved': return 'text-green-400 bg-green-500/10 border-green-500/20';
            case 'read': return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
            default: return 'text-amber-400 bg-amber-500/10 border-amber-500/20'; // Pending/Open
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'resolved': return 'Resolvido';
            case 'read': return 'Em Análise';
            default: return 'Pendente';
        }
    };

    return (
        <div className="fixed bottom-6 right-6 z-50">
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="absolute bottom-16 right-0 w-80 md:w-96 bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl overflow-hidden mb-2 flex flex-col max-h-[600px]"
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 p-4 shrink-0 flex justify-between items-center">
                            <div className="flex items-center gap-2 text-white">
                                <MessageSquarePlus className="h-5 w-5" />
                                <span className="font-semibold">Central de Feedback</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={refreshFeedbacks}
                                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-1.5 transition-colors"
                                    title="Atualizar"
                                >
                                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                                </button>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-1 transition-colors"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="flex bg-gray-800/50 p-1 m-2 rounded-lg shrink-0">
                            <button
                                onClick={() => setActiveTab('new')}
                                className={`flex-1 text-xs font-medium py-2 rounded-md transition-all ${activeTab === 'new'
                                    ? 'bg-violet-600 text-white shadow-lg'
                                    : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'}`}
                            >
                                Novo Feedback
                            </button>
                            <button
                                onClick={() => setActiveTab('history')}
                                className={`flex-1 text-xs font-medium py-2 rounded-md transition-all ${activeTab === 'history'
                                    ? 'bg-violet-600 text-white shadow-lg'
                                    : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'}`}
                            >
                                Meus Envios ({myFeedbacks.length})
                            </button>
                        </div>

                        {/* Body Scrollable */}
                        <div className="overflow-y-auto p-4 custom-scrollbar">
                            {activeTab === 'new' ? (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {/* Type Selection */}
                                    <div className="grid grid-cols-3 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setType('bug')}
                                            className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg text-xs font-medium transition-all border ${type === 'bug'
                                                ? 'bg-red-500/10 border-red-500/50 text-red-400'
                                                : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-750'
                                                }`}
                                        >
                                            <Bug className="h-4 w-4" />
                                            Erro/Bug
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setType('suggestion')}
                                            className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg text-xs font-medium transition-all border ${type === 'suggestion'
                                                ? 'bg-amber-500/10 border-amber-500/50 text-amber-400'
                                                : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-750'
                                                }`}
                                        >
                                            <Lightbulb className="h-4 w-4" />
                                            Sugestão
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setType('other')}
                                            className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg text-xs font-medium transition-all border ${type === 'other'
                                                ? 'bg-blue-500/10 border-blue-500/50 text-blue-400'
                                                : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-750'
                                                }`}
                                        >
                                            <MessageSquare className="h-4 w-4" />
                                            Outro
                                        </button>
                                    </div>

                                    {/* Message Input */}
                                    <div className="space-y-2">
                                        <label className="text-xs text-gray-400 ml-1">
                                            Sua mensagem <span className={message.length < 50 ? "text-red-400" : "text-green-400"}>({message.length}/50 min)</span>
                                        </label>
                                        <textarea
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder="Descreva o problema ou sua sugestão com detalhes..."
                                            className="w-full h-32 bg-gray-950/50 border border-gray-800 rounded-xl p-3 text-sm text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/50 resize-none"
                                            required
                                        />
                                    </div>

                                    {/* Footer Info */}
                                    <div className="flex items-start gap-2 p-2 rounded-lg bg-blue-900/10 border border-blue-800/20">
                                        <AlertCircle className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
                                        <p className="text-[10px] text-blue-300/80 leading-relaxed">
                                            Informações técnicas como URL atual e navegador são enviadas para análise. Limite de 1 envio por minuto.
                                        </p>
                                    </div>

                                    {/* Submit Button */}
                                    <Button
                                        type="submit"
                                        disabled={loading || message.length < 50}
                                        className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 border-0"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Enviando...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="mr-2 h-4 w-4" />
                                                Enviar Feedback
                                            </>
                                        )}
                                    </Button>
                                </form>
                            ) : (
                                <div className="space-y-3">
                                    {myFeedbacks.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500">
                                            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                            <p className="text-sm">Você ainda não enviou nenhum feedback.</p>
                                        </div>
                                    ) : (
                                        myFeedbacks.map((item) => (
                                            <div key={item.id} className="bg-gray-800/50 border border-gray-800 rounded-lg p-3 space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-semibold text-gray-400 bg-gray-900/50 px-2 py-1 rounded">
                                                        {new Date(item.createdat || item.created_at || item.createdAt || parseInt(item.id)).toLocaleDateString()}
                                                    </span>
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${getStatusColor(item.status)}`}>
                                                        {getStatusLabel(item.status)}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-300 line-clamp-3">
                                                    {item.message}
                                                </p>
                                                <div className="flex items-center gap-1 text-[10px] text-gray-500">
                                                    {getTypeIcon(item.type)}
                                                    <span className="capitalize">{item.type === 'bug' ? 'Erro/Bug' : item.type === 'suggestion' ? 'Sugestão' : 'Outro'}</span>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg shadow-violet-500/20 transition-colors ${isOpen
                    ? 'bg-gray-800 text-gray-400 rotate-45'
                    : 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white'
                    }`}
            >
                {isOpen ? <X className="h-6 w-6" /> : <Bug className="h-6 w-6" />}
            </motion.button>
        </div>
    );
};

export default FeedbackWidget;
